import { type Dtos } from "@inrev/common";
import { groupActiveProCons, type ProCon } from "@portal/api/decisioning";
import { useMemo } from "react";
import { cn } from "../../../../lib/utils";
import { EmptyState } from "../../../../patterns";
import { Button, Text } from "../../../../primitives";
import { useDecisioningPanel } from "../DecisioningPanelContext";
import { ImportanceBucket } from "./ImportanceBucket";

const COLUMN_COPY: Record<
	Dtos.Decisioning.ProConKind,
	{ title: string; addLabel: string; empty: string; rule: string }
> = {
	pro: {
		title: "Pros",
		addLabel: "Add a pro",
		empty: "No pros yet. Add the factor that most influenced your assessment first.",
		rule: "border-b-confirm",
	},
	con: {
		title: "Cons",
		addLabel: "Add a con",
		empty: "No cons yet. Add anything that weakens the account.",
		rule: "border-b-danger",
	},
};

export const columnJumpId = (kind: Dtos.Decisioning.ProConKind) => `column:${kind}` as const;

export type FactorColumnProps = {
	kind: Dtos.Decisioning.ProConKind;
	proCons: readonly ProCon[];
};

export const FactorColumn = ({ kind, proCons }: FactorColumnProps) => {
	const { openSheet } = useDecisioningPanel();
	const copy = COLUMN_COPY[kind];
	const buckets = useMemo(() => groupActiveProCons(proCons, kind), [proCons, kind]);
	const count = buckets.reduce((total, bucket) => total + bucket.items.length, 0);

	return (
		<section
			data-decisioning-item={columnJumpId(kind)}
			aria-label={copy.title}
			data-testid={`factor-column-${kind}`}
			className="flex min-w-0 flex-col gap-3"
		>
			<header
				className={cn("flex items-center justify-between gap-2 border-b-2 pb-1.5", copy.rule)}
			>
				<div className="flex items-baseline gap-2">
					<Text type="body-strong" color="primary">
						{copy.title}
					</Text>
					<Text type="caption" color="tertiary" data-testid={`factor-column-${kind}-count`}>
						{count}
					</Text>
				</div>
				<Button
					variant="ghost"
					size="sm"
					className="text-link"
					data-testid={`factor-column-${kind}-add`}
					onClick={() => openSheet({ kind: "factor", mode: "add", side: kind })}
				>
					{copy.addLabel}
				</Button>
			</header>
			{buckets.length === 0 ? (
				<EmptyState message={copy.empty} className="py-6" testId={`factor-column-${kind}-empty`} />
			) : (
				<div className="flex flex-col">
					{buckets.map((bucket) => (
						<ImportanceBucket
							key={bucket.importance}
							kind={kind}
							importance={bucket.importance}
							items={bucket.items}
						/>
					))}
				</div>
			)}
		</section>
	);
};
